/**
 * Workspace switcher — chip in the app header. Click to:
 *   - See every workspace I'm a member of
 *   - Switch the active one (persists to users.active_tenant_id,
 *     refreshes the page so all scoped queries pick up the new
 *     tenant)
 *   - Create a new workspace (becomes admin, optionally switches in)
 *   - Open the workspace's settings page
 *
 * Replaces the inert "/ TenantName" chip that used to sit in Layout.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { Link, useRouter } from '../router';
import { IconCheck, IconChevronRight, IconCog, IconPlus } from './Icon';
import { Spinner } from './Spinner';

interface Workspace {
  id: string;
  slug: string;
  name: string;
  isPersonal: boolean;
  role: 'admin' | 'member';
}

export function WorkspaceSwitcher({
  current,
  workspaces,
  onChange,
}: {
  current: { id: string; name: string } | null;
  workspaces: Workspace[];
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  async function switchTo(id: string) {
    if (id === current?.id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.switchWorkspace(id);
      // Track the switch before the reload so the event makes it to
      // PostHog. Group is set by the reloaded App's /api/me handler.
      try {
        const target = workspaces.find((w) => w.id === id);
        const { track } = await import('../lib/analytics');
        track('workspace_switched', {
          from_workspace_id: current?.id ?? null,
          to_workspace_id: id,
          to_workspace_slug: target?.slug ?? null,
          to_is_personal: target?.isPersonal ?? null,
        });
      } catch {
        /* analytics off — fine */
      }
      // Full reload so every cached query (boards, tasks, intents)
      // re-fetches scoped to the new active workspace. Cheap and
      // bulletproof — better than threading a "reload all" event.
      window.location.assign('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
      setBusy(false);
    }
  }

  async function createWorkspace(e: React.FormEvent) {
    e.preventDefault();
    const name = createName.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.createWorkspace(name, true);
      window.location.assign('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
      setBusy(false);
    }
  }

  if (!current) return null;

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="hidden items-center gap-1 text-xs text-cream-3 underline-offset-2 hover:text-cream-2 sm:inline-flex"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Workspace: ${current.name}. Click to switch.`}
        >
          <span className="text-cream-4">/</span>
          <span className="font-medium">{current.name}</span>
          <IconChevronRight
            size={11}
            style={{
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 180ms var(--ease-out-soft, ease)',
            }}
          />
        </button>

        {open && (
          <div
            ref={popoverRef}
            role="menu"
            aria-label="Workspaces"
            className="mention-popover absolute left-0 top-full mt-2 z-40"
            style={{ width: 280 }}
          >
            <div className="border-b border-ink-rim px-3 py-1.5 text-[10px] uppercase tracking-wide text-cream-3">
              Workspaces
            </div>
            <ul className="flex max-h-[40vh] flex-col overflow-y-auto py-1">
              {workspaces.map((w) => {
                const active = w.id === current.id;
                return (
                  <li key={w.id}>
                    <button
                      type="button"
                      className="mention-popover-row"
                      data-active={active}
                      disabled={busy}
                      onClick={() => switchTo(w.id)}
                    >
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{
                          background: active
                            ? 'var(--color-clove)'
                            : w.isPersonal
                              ? 'var(--color-leaf)'
                              : 'var(--color-cream-3)',
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">{w.name}</span>
                      {w.isPersonal && (
                        <span className="text-[10px] text-cream-3">personal</span>
                      )}
                      {w.role === 'admin' && !w.isPersonal && (
                        <span className="text-[10px] text-cream-3">admin</span>
                      )}
                      {active && <IconCheck size={12} />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-col gap-1 border-t border-ink-rim p-1.5">
              <button
                type="button"
                className="mention-popover-row text-cream-2"
                onClick={() => {
                  setOpen(false);
                  navigate('/tenant');
                  onChange();
                }}
              >
                <IconCog size={12} />
                <span>Workspace settings</span>
              </button>
              <button
                type="button"
                className="mention-popover-row text-cream-2"
                onClick={() => {
                  setOpen(false);
                  setCreateName('');
                  setCreateOpen(true);
                }}
              >
                <IconPlus size={12} />
                <span>Create workspace</span>
              </button>
            </div>
            {error && (
              <p className="border-t border-ink-rim px-3 py-2 text-[11px] text-rust">
                {error}
              </p>
            )}
          </div>
        )}
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create a workspace" maxWidth={420}>
        <form className="flex flex-col gap-3" onSubmit={createWorkspace}>
          <p className="text-xs text-cream-3">
            A new workspace you'll own. You can invite collaborators in its settings. Existing
            members of your other workspaces won't see it unless you invite them.
          </p>
          <label className="flex flex-col gap-1 text-xs text-cream-3">
            Name
            <input
              className="input text-sm"
              autoFocus
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Little Dog"
              maxLength={80}
            />
          </label>
          {error && <p className="text-xs text-rust">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setCreateOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary inline-flex items-center gap-2"
              disabled={busy || !createName.trim()}
            >
              {busy && <Spinner size={12} />}
              Create &amp; switch
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// Suppress unused-import warning — keeping Link in scope for future menu items.
void Link;
